import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import Editor from "@monaco-editor/react";
import { Button, Form, Spinner, Table, Alert } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import "./CodingChallenge.scss";
import { FaAngleDown, FaPlay, FaTerminal } from "react-icons/fa";
import { getCurrentUser } from "../../utils/auth";
import { fetchJudgeLanguages, toSandboxLanguages } from "../../utils/judgeLanguages";

const CodingChallenge = forwardRef(({
  question = "Print Hello, World!",
  sampleCode = {
    java: `public class Main {
  public static void main(String[] args) {
    System.out.println("Hello, World!");
  }
}`,
    javascript: `console.log("Hello, World!");`
  },
  testCases = [{ input: "", expected: "Hello, World!" }],
  challengeId,
  description = "Write a program to print 'Hello, World!' in the specified language.",
  onSubmit,
  disabled
}, ref) => {

  const [lang, setLang] = useState("");
  const [code, setCode] = useState("");
  const [languages, setLanguages] = useState([]);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const editorRef = useRef(null);
  const [activity, setActivity] = useState("");
  const loggedInUser = getCurrentUser();
  const isDevMode = loggedInUser?.mode === "dev" || false;

  // Load languages from Judge0 API (cached in sessionStorage)
  useEffect(() => {
    fetchJudgeLanguages().then(raw => {
      const langs = toSandboxLanguages(raw);
      setLanguages(langs);
      if (langs.length > 0 && !lang) {
        const first = langs.find(l => sampleCode?.[l.value]) || langs[0];
        setLang(first.value);
        setCode(sampleCode?.[first.value] || "");
      }
    });
  // eslint-disable-next-line
  }, []);

  useImperativeHandle(ref, () => ({
    runAllTests: handleRunAllCases,
    getCode: () => code,
    setCode: newCode => setCode(newCode),
    getResults: () => results,
    autoRunAndSubmit: async () => {
      await handleRunAllCases("submit");
    }
  }));

  const onMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.focus();

    editor.onKeyDown((event) => {
      if (isDevMode) return; // Skip tracking in dev mode
      const { keyCode, ctrlKey, metaKey } = event;
      if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
        event.preventDefault();
        setActivity("copypaste");
      }
    });
  };

  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setLang(value);
    setCode(sampleCode?.[value] || "");
    setResults([]);
    setOutput("");
    setInput("");
    setError("");
  };

  const handleRun = async () => {
    setRunning(true);
    setResults([]);
    setOutput("");
    setError("");
    const langId = (languages.find(l => l.value === lang) || {}).id;
    if (!langId) return;
    try {
      const { data } = await apiClient.post("/code/run", {
        source_code: code,
        language_id: langId,
        stdin: input,
      });
      const runOutput = (data.stdout || data.stderr || data.compile_output || "").trim();
      setOutput(runOutput);
    } catch (err) {
      setError("Could not run code. Please try again.");
    }
    setRunning(false);
  };

  const handleRunAllCases = async (action) => {
    setRunning(true);
    setResults([]);
    setError("");
    const langId = (languages.find(l => l.value === lang) || {}).id;
    if (!langId) return;
    try {
      const { data } = await apiClient.post("/code/run-batch", {
        source_code: code,
        language_id: langId,
        testCases: testCases.map(tc => ({ input: tc.input, expected: tc.expected })),
      });
      setResults(data.results);
      if (action === "submit") {
        await handleSubmit(data.results);
      }
    } catch (err) {
      setError("Could not run test cases. Please try again.");
    }
    setRunning(false);
  };

  const handleSubmit = async (e) => {
    setSubmitted(true);
    console.log("submitting manually", e, results);
    let fresults = null;
    if (Array.isArray(e)) {
      fresults = e;
    } else if (Array.isArray(results)) {
      fresults = results;
    }
    console.log("Submitting code...", fresults);
    if (!Array.isArray(fresults) || fresults.length === 0) {
      setError("No test cases run. Please run the code first.");
      return;
    }
    console.log("Submitting results:", fresults);
    const passedCount = Array.isArray(fresults) ? fresults.filter(r => r.passed).length : 0;
    const total = Array.isArray(fresults) ? fresults.length : 0;
    const allPassed = passedCount === total;

    if (onSubmit) {
      await onSubmit({
        questionId: challengeId,
        code,
        language: lang,
        results: fresults,
        passedCount,
        total,
        status: allPassed ? "passed" : "failed",
        notes: allPassed
          ? "All test cases passed"
          : `Failed ${total - passedCount} out of ${total} test cases`
      });
    }
  };

    return (
      <>
        <div className="fs-6 fw-semibold mb-2">Question:</div>
        <div className="mb-2 text-lg">
          <div dangerouslySetInnerHTML={{ __html: question }} />
        </div>
        <div className="mb-3 text-md">
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </div>
        <div className="coding-challenge-dark p-0">
          <div className="toolbar">
            <div className="position-relative">
              <Form.Select
                value={lang}
                className="language-dropdown pe-5"
                onChange={handleLanguageChange}
                style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
                disabled={languages.length === 0}
              >
                {languages.some(l => sampleCode?.[l.value]) && (
                  <optgroup label="⭐ Recommended">
                    {languages.filter(l => sampleCode?.[l.value]).map(l => (
                      <option key={l.id} value={l.value}>{l.label}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Other Languages">
                  {languages.filter(l => !sampleCode?.[l.value]).map(l => (
                    <option key={l.id} value={l.value}>{l.label}</option>
                  ))}
                </optgroup>
              </Form.Select>
              {/* Dropdown icon */}
              <span className="dropdown-icon">
                <FaAngleDown />
              </span>
            </div>
            <Button className="run-btn ms-auto" onClick={handleRun} disabled={running}>
              {running ? <Spinner size="sm" animation="border" /> : <><FaPlay /> Run Code</>}
            </Button>
            <Button variant="outline-info" className="ms-3" onClick={handleRunAllCases} disabled={running}>
              {running ? <Spinner size="sm" animation="border" /> : <><FaTerminal />Test All Cases</>}
            </Button>
            <Button
              variant="success"
              className="ms-3"
              onClick={handleSubmit}
              disabled={disabled || !results.length}
            >
              Submit
            </Button>
          </div>
          {/* Main Row */}
          <div className="main-row">
            {/* Code Editor */}
            <div className="left-col">
              <Editor
                height="calc(70vh - 40px)"
                language={lang}
                value={code}
                onChange={setCode}
                theme="vs-dark"
                options={{ fontSize: 15, formatOnType: true, minimap: { enabled: false }, wordWrap: true, contextmenu: false }}
                onMount={onMount}
              />
            </div>
            {/* Input/Output */}
            <div className="right-col">
              <div>
                <div className="box-label">Input</div>
                <Form.Control
                  as="textarea"
                  rows={7}
                  className="input-area"
                  placeholder="Enter input here..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  spellCheck={false}
                />
              </div>
              <div>
                <div className="box-label">Output</div>
                <Form.Control
                  as="textarea"
                  rows={7}
                  className="output-area"
                  value={output}
                  readOnly
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
          {/* Results Table */}
          {results.length > 0 && (
            <Table bordered size="sm" className="results-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Input</th>
                  <th>Expected Output</th>
                  <th>Your Output</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><pre className="mb-0">{r.input || "stdin empty"}</pre></td>
                    <td><pre className="mb-0">{r.expected}</pre></td>
                    <td><pre className="mb-0">{r.output}</pre></td>
                    <td>
                      <span className={`badge ${r.passed ? "bg-success" : "bg-danger"}`}>
                        {r.passed ? "Passed" : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {submitted && !error && (
            <Alert variant="success" className="mt-3">Code submitted successfully!</Alert>
          )}
          {/* Question & Test Case Info */}
          <div className="p-4">
            <div className="fs-6 fw-semibold mb-2">Test Cases:</div>
            <Table bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Input</th>
                  <th style={{ width: 200 }}>Expected Output</th>
                </tr>
              </thead>
              <tbody>
                {testCases.filter(tc => !tc.isHidden).map((tc, i) => (
                  <tr key={i}>
                    <td><pre className="mb-0">{tc.input || "stdin empty"}</pre></td>
                    <td><pre className="mb-0">{tc.expected}</pre></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </>
    );
});

export default CodingChallenge;
